<script setup lang="ts">
import {computed, onMounted, ref} from 'vue'
import {storeToRefs} from 'pinia'
import {Eye, RefreshCw, Save, Send} from 'lucide-vue-next'
import {useModelSettingsStore} from '@/stores/modelSettings'
import {useUserStore} from '@/stores/user'
import {cpApi} from '@/api'
import {Button} from '@/components/ui/button'
import ModelRoleRow from '@/components/ModelRoleRow.vue'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import type {ModelRole, ReasoningEffort} from '@shared/llm_types'

type Draft = {model: string | null; reasoning: ReasoningEffort | null}
type DigestError = '' | 'profile' | 'preview' | 'send'
const store = useModelSettingsStore()
const {catalog, roles, isLoading, isRefreshing, savingRole, error, isEmpty} = storeToRefs(store)
const drafts = ref<Partial<Record<ModelRole, Draft>>>({})
const isDirty = computed(() => Object.keys(drafts.value).length > 0)
const userStore = useUserStore()
const digestError = ref<DigestError>('')
const isProfileLoading = ref(true)
const isPreviewLoading = ref(false)
const isSendPreviewLoading = ref(false)
const digestSent = ref(false)
const showPreview = ref(false)
const previewSubject = ref('')
const previewHtml = ref('')
const activeProfileId = computed(() => userStore.profile?.profile_id ?? null)

function updateDraft(role: ModelRole, model: string | null, reasoning: ReasoningEffort | null): void {
  drafts.value[role] = {model, reasoning}
}

async function saveChanges(): Promise<void> {
  for (const role of roles.value) {
    const draft = drafts.value[role.role]
    if (draft && (await store.save(role.role, draft.model, draft.reasoning))) delete drafts.value[role.role]
  }
}

async function openDigestPreview(): Promise<void> {
  if (!activeProfileId.value) return
  isPreviewLoading.value = true
  digestError.value = ''
  digestSent.value = false
  try {
    const preview = await cpApi.emailDigest.preview(activeProfileId.value)
    previewSubject.value = preview.subject
    previewHtml.value = preview.html
    showPreview.value = true
  } catch {
    digestError.value = 'preview'
  } finally {
    isPreviewLoading.value = false
  }
}

async function sendDigestPreview(): Promise<void> {
  if (!activeProfileId.value) return
  isSendPreviewLoading.value = true
  digestError.value = ''
  digestSent.value = false
  try {
    await cpApi.emailDigest.sendPreview(activeProfileId.value)
    digestSent.value = true
  } catch {
    digestError.value = 'send'
  } finally {
    isSendPreviewLoading.value = false
  }
}

onMounted(() => {
  void store.load()
  void userStore
    .ensureProfileSelected()
    .catch(() => {
      digestError.value = 'profile'
    })
    .finally(() => {
      isProfileLoading.value = false
    })
})
</script>

<template>
  <Teleport to="#app-header-left" defer>
    <h1 class="max-w-[36vw] truncate text-sm font-medium">{{ $t('cp.modelSettingsTitle') }}</h1>
    <span class="whitespace-nowrap text-xs text-muted-foreground">{{ catalog.modelCount }} {{ $t('cp.models') }}</span>
    <span
      v-if="catalog.lastSuccessfulRefreshAt"
      class="hidden whitespace-nowrap text-xs text-muted-foreground xl:inline"
      >{{ catalog.lastSuccessfulRefreshAt }}</span
    >
  </Teleport>
  <Teleport to="#app-header-right" defer>
    <Button
      variant="outline"
      size="sm"
      :aria-label="$t('cp.refreshCatalog')"
      :disabled="isRefreshing"
      @click="store.refresh"
      ><RefreshCw class="size-3.5 sm:mr-1.5" :class="isRefreshing && 'animate-spin'" /><span class="hidden sm:inline">{{
        $t('cp.refreshCatalog')
      }}</span></Button
    >
    <Button
      size="sm"
      :aria-label="$t('cp.saveSettings')"
      :disabled="!isDirty || savingRole !== null"
      @click="saveChanges"
      ><Save class="size-3.5 sm:mr-1.5" /><span class="hidden sm:inline">{{ $t('cp.saveSettings') }}</span></Button
    >
  </Teleport>
  <section class="space-y-3 p-3 sm:p-4">
    <p v-if="error" class="rounded-md border border-destructive px-3 py-2 text-sm text-destructive">{{ error }}</p>
    <div class="grid gap-4 rounded-md border p-4">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="space-y-1">
          <h2 class="font-medium">{{ $t('cp.emailDigestTitle') }}</h2>
          <p class="max-w-2xl text-sm text-muted-foreground">{{ $t('cp.emailDigestDescription') }}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          :disabled="!activeProfileId || isProfileLoading || isPreviewLoading"
          data-testid="email-digest-preview"
          @click="openDigestPreview">
          <Eye class="mr-1.5 size-3.5" />{{ $t('cp.emailDigestPreview') }}
        </Button>
      </div>
      <p v-if="!activeProfileId" class="text-xs text-muted-foreground">{{ $t('cp.emailDigestPreviewNoProfile') }}</p>
      <p v-if="digestError" class="text-sm text-destructive">
        {{
          digestError === 'profile'
            ? $t('cp.emailDigestProfileLoadError')
            : digestError === 'send'
              ? $t('cp.emailDigestSendError')
              : $t('cp.emailDigestPreviewError')
        }}
      </p>
    </div>
    <p v-if="!isLoading && isEmpty && !error" class="py-4 text-center text-sm text-muted-foreground">
      {{ $t('cp.emptyCatalog') }}
    </p>
    <div v-if="isLoading" class="py-4 text-center text-sm text-muted-foreground">{{ $t('common.loading') }}</div>
    <div v-else-if="roles.length" class="overflow-x-auto rounded-md border">
      <div class="min-w-0 divide-y">
        <div
          class="hidden grid-cols-[minmax(10rem,1fr)_minmax(16rem,2fr)_12rem_9rem] gap-2 border-b bg-muted/30 px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground lg:grid">
          <span>{{ $t('cp.roleColumn') }}</span>
          <span>{{ $t('cp.modelColumn') }}</span>
          <span>{{ $t('cp.priceColumn') }}</span>
          <span>{{ $t('cp.reasoningColumn') }}</span>
        </div>
        <ModelRoleRow
          v-for="role in roles"
          :key="role.role"
          :role="role"
          @change="(model, reasoning) => updateDraft(role.role, model, reasoning)" />
      </div>
    </div>
  </section>
  <Dialog v-model:open="showPreview">
    <DialogContent class="h-[90vh] max-w-5xl grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
      <DialogHeader>
        <DialogTitle>{{ $t('cp.emailDigestPreviewTitle') }} — {{ previewSubject }}</DialogTitle>
        <DialogDescription>{{ $t('cp.emailDigestPreviewDescription') }}</DialogDescription>
      </DialogHeader>
      <iframe
        v-if="previewHtml"
        :srcdoc="previewHtml"
        class="h-full min-h-0 w-full rounded-md border bg-slate-100"
        :title="$t('cp.emailDigestPreviewTitle')"
        sandbox="" />
      <DialogFooter>
        <p v-if="digestSent" class="mr-auto text-sm text-emerald-600">{{ $t('cp.emailDigestSendSuccess') }}</p>
        <p v-if="digestError === 'send'" class="mr-auto text-sm text-destructive">
          {{ $t('cp.emailDigestSendError') }}
        </p>
        <Button
          type="button"
          :disabled="isSendPreviewLoading"
          data-testid="email-digest-send-preview"
          @click="sendDigestPreview">
          <Send class="mr-1.5 size-3.5" />{{ $t('cp.emailDigestSendPreview') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
